import { defineComponent, ref, onMounted } from 'vue'
import styles from './index.module.scss'
import getMapScript from './utils/getMapScript'
import { MapContext } from './index.model'
import { IPoint } from '@/contract/map.base'
import defaultSettings from './utils/defaultSettings'
import { IMapSetting } from './index.model'
import { MapStyleLoader } from './utils/MapStyleLoader'

// 地图原子组件,用于初始化地图
let mapIndex = 0
export default defineComponent({
  name: 'nio-map',
  props: {
    removeLogo: {
      type: Boolean,
      default: false,
    },
    preserveDrawingBuffer: {
      type: Boolean,
      default: false,
    },
    initSetConfig: {
      type: Object as () => IMapSetting,
      default: {
        enableAutoResize: false, // 可以resize
        enableRotate: false, // 禁止旋转地图
        enableTilt: false,
        enableRotateGestures: false,
        enableTiltGestures: false,
      } as IMapSetting,
    },
  },
  // onReady: 地图初始化完成后的回调函数
  emits: ['ready'],
  setup: (props, { emit }) => {
    const win: any = window
    let BMapGL = null as any
    let mapInstance = null as any
    // 确保ID唯一
    const mapId = ref(`map${++mapIndex}`)
    // 样式加载器实例
    let styleLoader: MapStyleLoader | null = null

    // ——————————————暴露方法——————————————
    /**
     * 获取实例
     * @returns
     */
    const getMap = () => {
      return mapInstance
    }

    /**
     *
     * @returns 获取地图中心点
     */
    const getCenter = () => {
      const c = mapInstance.getCenter()
      return {
        lng: c.lng,
        lat: c.lat,
      } as IPoint
    }

    /**
     * 设置地图中心点
     * @param center
     * @param noAnimation
     * @returns
     */
    const setCenter = (center: IPoint, noAnimation?: boolean) => {
      return mapInstance.setCenter(new BMapGL.Point(center.lng, center.lat), {
        noAnimation: noAnimation,
      })
    }

    /**
     * 获取zoom
     * @returns
     */
    const getZoom = () => {
      return mapInstance.getZoom()
    }

    /**
     * 设置zoom
     * @param zoom
     * @param noAnimation
     * @returns
     */
    const setZoom = (zoom: number, noAnimation?: boolean) => {
      return mapInstance.setZoom(zoom, {
        noAnimation: noAnimation,
      })
    }

    /**
     * 设置地图允许的最小级别
     * @param zoom
     */
    const setMinZoom = (zoom: number) => {
      mapInstance.setMinZoom(zoom)
    }

    /**
     * 设置地图允许的最大级别
     * @param zoom
     */
    const setMaxZoom = (zoom: number) => {
      mapInstance.setMaxZoom(zoom)
    }

    /**
     * 启用滚轮放大缩小
     * @param enable
     */
    const enableScrollWheelZoom = (enable: boolean) => {
      enable ? mapInstance.enableScrollWheelZoom() : mapInstance.disableScrollWheelZoom()
    }

    /**
     * 启用双指缩放地图
     * @param enable
     */
    const enablePinchToZoom = (enable: boolean) => {
      enable ? mapInstance.enablePinchToZoom() : mapInstance.disablePinchToZoom()
    }

    /**
     * 展示或隐藏地图上的文字和标记
     * @param show
     */
    const showMapText = (show: boolean) => {
      mapInstance.setDisplayOptions({
        poiText: show,
        poiIcon: show,
        street: show,
      })
    }

    /**
     * 设置地图的主题
     * @param theme
     * @param maxRetries 最大重试次数，默认3次
     */
    const setMapStyle = (theme?: Array<any>, maxRetries = 3) => {
      if (!mapInstance) return

      const styleJson = theme || defaultSettings.theme

      // 清理之前的加载器实例
      if (styleLoader) {
        styleLoader.destroy()
        styleLoader = null
      }

      // 创建新的样式加载器并加载
      styleLoader = new MapStyleLoader(mapInstance, styleJson, maxRetries)
      styleLoader.load()
    }

    const context = {
      getMap, // 获取地图实例
      getCenter,
      setCenter,
      getZoom,
      setZoom,
      setMinZoom,
      setMaxZoom,
      enableScrollWheelZoom,
      enablePinchToZoom,
      showMapText,
      setMapStyle,
    } as MapContext

    const setMapDefaultConfig = () => {
      enableScrollWheelZoom(defaultSettings.scrollWheelZoom as boolean) // 启用滚轮放大缩小
      enablePinchToZoom(defaultSettings.pinchToZoom as boolean) // 启用双指缩放地图
      mapInstance.setMapType(win.BMAP_NORMAL_MAP)
    }

    const initMap = (cls: any) => {
      if (!document.querySelector(`#${mapId.value}`)) {
        return
      }
      // 储存构造函数及创建Map实例
      BMapGL = cls
      win.BMapGL = BMapGL // 其他lib里面用的都是BMap，保持名称一致
      mapInstance = new BMapGL.Map(`${mapId.value}`, {
        preserveDrawingBuffer: props.preserveDrawingBuffer, // 用于截图
      })
      // mapInstance.centerAndZoom(new BMapGL.Point(116.28019, 40.049191), 19)
      mapInstance._setConfig(props.initSetConfig)
      // 初始化地图设置
      setMapDefaultConfig()
      // 回调函数， 可以通过构造函数和Map实例自行配置
      emit('ready', { mapInstance, context }) // 必须在setMapDefaultConfig后ready 否则大公鸡不会适配屏幕
    }

    onMounted(() => {
      // 获取script并初始化地图
      getMapScript().then(initMap)
    })

    return () => (
      <>
        <div class={[styles.mapContainer, styles.removeLogo]} id={mapId.value}></div>
        {!props.removeLogo && (
          <div class={[styles.copyright]}>
            <img src="https://api.map.baidu.com/images/logo_hd.png" alt="" />
            <span>© 2023 Baidu - GS(2023)3206号 - 甲测资字11111342 - 京ICP证030173号</span>
          </div>
        )}
      </>
    )
  },
})
